import { Button, Flex, Popover, Text, Box, Grid } from '@radix-ui/themes';
import { useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { sv } from 'date-fns/locale';

interface DatePickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export function DatePicker({ selectedDate, onDateChange }: DatePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(selectedDate));
  const [open, setOpen] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

  const handleDateSelect = (date: Date) => {
    onDateChange(date);
    setOpen(false);
  };

  const today = new Date();

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button variant="outline" size="2" aria-label="Välj datum">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {format(selectedDate, 'd MMM yyyy', { locale: sv })}
        </Button>
      </Popover.Trigger>
      <Popover.Content style={{ width: 280 }}>
        <Flex direction="column" gap="3">
          <Flex justify="between" align="center">
            <Button variant="ghost" size="1" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} aria-label="Föregående månad">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Button>
            <Text weight="medium">{format(currentMonth, 'MMMM yyyy', { locale: sv })}</Text>
            <Button variant="ghost" size="1" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} aria-label="Nästa månad">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Button>
          </Flex>

          <Grid columns="7" gap="1">
            {weekDays.map((day) => (
              <Box key={day} style={{ textAlign: 'center', padding: 4 }}>
                <Text size="1" color="gray" weight="medium">
                  {day}
                </Text>
              </Box>
            ))}
            {days.map((day) => {
              const isSelected = isSameDay(day, selectedDate);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, today);
              const isFuture = day > today && !isSameDay(day, today);

              return (
                <Button
                  key={day.toISOString()}
                  variant={isSelected ? 'solid' : 'ghost'}
                  size="1"
                  disabled={isFuture}
                  onClick={() => handleDateSelect(day)}
                  style={{
                    opacity: isCurrentMonth ? 1 : 0.4,
                    fontWeight: isToday ? 'bold' : 'normal',
                    border: isToday && !isSelected ? '1px solid var(--accent-9)' : undefined,
                  }}
                >
                  {format(day, 'd')}
                </Button>
              );
            })}
          </Grid>

          <Flex gap="2">
            <Button
              variant="soft"
              size="1"
              style={{ flex: 1 }}
              onClick={() => {
                setCurrentMonth(startOfMonth(today));
                handleDateSelect(today);
              }}
            >
              Idag
            </Button>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
